import { normalizeAuthEmail } from "../components/auth/utils/normalizeCredentials";

const SESSION_GENERATION_KEY = "session-generation";

export type SessionChangeReason =
  | { type: "logout" }
  // `email` is the identity that just signed in — not a secret (it's
  // already visible in this same browser's own UI/DOM), included purely so
  // other tabs can tell "a different identity took over" apart from "the
  // person I already am signed in again elsewhere".
  | { type: "sign-in"; email: string | null };

/**
 * Announces that this tab just changed the browser's one shared auth
 * session — either by signing in or by logging out.
 *
 * The refresh token lives in an httpOnly cookie scoped to the browser, not
 * to a tab, so every open tab is really sharing a single session, not one
 * each. Left to discover this on their own, other tabs would keep running
 * on stale in-memory state until their own access token expires:
 *   - after a new sign-in AS A DIFFERENT IDENTITY, that means silently
 *     refreshing into the *new* session while still showing the *old*
 *     identity's UI;
 *   - after a logout, that means staying usable (and, on a shared/walk-up
 *     computer, visibly "logged in") long after the user believed logging
 *     out ended the session everywhere.
 *
 * This announcement lets every other tab react immediately instead of
 * drifting into either state. The `storage` event this fires in every
 * other same-origin tab is the signal.
 *
 * IMPORTANT: call this from the actual sign-in/logout call sites, never
 * from inside setTokens()/clearAuth() themselves — the receiving tab's
 * reaction to this signal is to call clearAuth(), and clearAuth() calling
 * this in turn would ping-pong the two tabs into an infinite loop.
 */
export function announceSessionChanged(reason: SessionChangeReason): void {
  localStorage.setItem(
    SESSION_GENERATION_KEY,
    JSON.stringify({
      ...reason,
      // `storage` events only fire when the stored value actually changes,
      // so two announcements with the same `reason` shape back-to-back
      // (e.g. logout, then logout again) would silently collapse into one
      // event without something to force each write to be distinct.
      // `nonce` exists purely for that — a fresh random value nobody reads
      // for its content, only to guarantee "this write is new". `signaledAt`
      // is not read by any consumer either; it's here only so anyone
      // inspecting this key in DevTools can tell when it was last written.
      nonce: Math.random(),
      signaledAt: Date.now(),
    }),
  );
}

/**
 * Subscribes to other tabs' announceSessionChanged() calls.
 *
 * The `storage` event never fires in the tab that made the write, only in
 * every other same-origin tab — exactly the "the shared session just
 * changed elsewhere" signal this is meant to catch. Returns an unsubscribe
 * function.
 *
 * A malformed/unparseable payload is treated as a logout — fail closed
 * rather than silently ignoring a real session change.
 */
export function subscribeToSessionChangeElsewhere(
  onSessionChanged: (reason: SessionChangeReason) => void,
): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== SESSION_GENERATION_KEY || !e.newValue) return;
    try {
      onSessionChanged(JSON.parse(e.newValue) as SessionChangeReason);
    } catch {
      onSessionChanged({ type: "logout" });
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

/**
 * Decides whether a session-change announcement from another tab should
 * force this tab to sign out.
 *
 * - A logout always forces sign-out, unconditionally — it's an explicit
 *   action ending the one shared session, regardless of who else is on it.
 * - A sign-in only forces sign-out when it's a genuinely different
 *   identity. The same person signing in again in another tab isn't a
 *   security event for this tab: this tab's UI already shows the right
 *   person, and its next refresh will still ride on a legitimate session
 *   for that same account — forcing it out would be pure disruption.
 * - Any ambiguity (either identity unknown/unresolved) fails closed to
 *   forcing sign-out, matching the conservative default this whole
 *   mechanism was built around.
 */
export function shouldForceSignOut(
  reason: SessionChangeReason,
  currentEmail: string | null,
): boolean {
  if (reason.type === "logout") return true;
  if (!reason.email || !currentEmail) return true;
  return normalizeAuthEmail(reason.email) !== normalizeAuthEmail(currentEmail);
}
